/**
 * The one place a figure's "?" text is defined.
 *
 * THUNKS, NOT STRINGS AND NOT KEYS. A translation call at module scope would
 * resolve once at import and freeze the language. A table of key strings
 * resolved later is language-safe but invisible to localeKeyIntegrity, whose
 * scan sees only literal calls (localeKeyIntegrity.test.ts:43-48). A thunk
 * holding one literal call is both: resolved at render, visible to the scan.
 *
 * NO CALL SHAPES IN COMMENTS. That scan reads raw source, comments included, so
 * a comment quoting a call with a quoted key becomes a key both locales must
 * hold. In prose, write t(<key>).
 *
 * @module components/help/metricHelpRegistry
 */

/** "Not measurable / not known". Never a zero. A line that needs it is dropped. */
export const NOT_MEASURED = "—";

/** useTranslation()'s t and i18n.getFixedT(lng) both assign to this. */
export type HelpT = (key: string, options?: Record<string, string>) => string;

/** Every id /help renders. Help.tsx types its sections, and helpContent its definitions, with it. */
export const HELP_ANCHORS = [
  "limits", "coverage", "rules", "results", "definitions", "budget", "platforms",
  "def-conversions", "def-cpl", "def-cpa", "def-cpc", "def-ctr", "def-cvr",
  "def-cpm", "def-roas", "def-leads", "def-lead-to-sale",
] as const;
export type HelpAnchor = (typeof HELP_ANCHORS)[number];
export const isHelpAnchor = (id: string): id is HelpAnchor =>
  (HELP_ANCHORS as readonly string[]).includes(id);

export type HelpPage = "dashboard";

export interface HelpLine<P extends string = string> {
  /** Exactly the {{placeholders}} this line's EN and TH strings use. */
  readonly needs: readonly P[];
  /** The live computation. At most one per entry; rendered set apart. */
  readonly formula?: true;
  /**
   * ONE literal translation call whose options object lists exactly `needs`,
   * each as v.<name> (no spread). The parameter MUST be named `t` and the key
   * MUST be a quoted literal. A static line is written (t) => …, because an
   * unused `v` fails noUnusedParameters (tsconfig.json:20).
   */
  readonly text: (t: HelpT, v: Readonly<Record<P, string>>) => string;
}

export interface MetricHelpEntry<L extends readonly HelpLine<string>[] = readonly HelpLine<string>[]> {
  readonly page: HelpPage;
  /** "Read more" goes to /help#<anchor>. */
  readonly anchor: HelpAnchor;
  /** The ON-SCREEN label's own key, so the title follows every label fix. No placeholders. */
  readonly title: (t: HelpT) => string;
  /**
   * Where on the page this copy of the figure sits. Required when another entry
   * on the same page has the same title, so every trigger's accessible name is
   * unique (registry test). No placeholders.
   */
  readonly where?: (t: HelpT) => string;
  /** 1–5 lines, display order: what → formula → date range → "—" → extra. */
  readonly lines: L;
}

const line = <const P extends string = never>(l: HelpLine<P>): HelpLine<P> => l;
const entry = <const L extends readonly HelpLine<string>[]>(e: MetricHelpEntry<L>): MetricHelpEntry<L> => e;

// ---- where on the page --------------------------------------------------------
const WHERE_BAND = (t: HelpT): string => t("metricHelp.whereBand");
const WHERE_KPI = (t: HelpT): string => t("metricHelp.whereKpi");

// ---- lines (the catalogue's copy, one const per key) ---------------------------
const RANGE_SELECTED = line({
  needs: ["range"],
  text: (t, v) => t("metricHelp.rangeSelected", { range: v.range }),
});
const RANGE_LAST30 = line({
  needs: ["range30"],
  text: (t, v) => t("metricHelp.rangeLast30", { range30: v.range30 }),
});
const RANGE_ALL_TIME = line({ needs: [], text: (t) => t("metricHelp.rangeAllTime") });
const COMPARE_PREV = line({
  needs: ["prevRange", "before"],
  text: (t, v) => t("metricHelp.comparePrev", { prevRange: v.prevRange, before: v.before }),
});
const SPEND_WHAT = line({ needs: [], text: (t) => t("metricHelp.spendWhat") });
const SPEND_CALC = line({
  needs: ["spend", "byPlatform"],
  formula: true,
  text: (t, v) => t("metricHelp.spendCalc", { spend: v.spend, byPlatform: v.byPlatform }),
});
const SPEND_NO_COMPARE = line({ needs: [], text: (t) => t("metricHelp.spendNoCompare") });
const SPEND_DASH = line({ needs: [], text: (t) => t("metricHelp.spendDash") });
const SPEND_STOPPED = line({ needs: [], text: (t) => t("metricHelp.spendStopped") });
const CONVERSIONS_WHAT = line({ needs: [], text: (t) => t("metricHelp.conversionsWhat") });
const CONVERSIONS_BASKET = line({ needs: [], text: (t) => t("metricHelp.conversionsBasket") });
const CONVERSIONS_FRACTIONAL = line({ needs: [], text: (t) => t("metricHelp.conversionsFractional") });
const CONVERSIONS_NO_COMPARE = line({ needs: [], text: (t) => t("metricHelp.conversionsNoCompare") });
const CPA_WHAT = line({ needs: [], text: (t) => t("metricHelp.cpaWhat") });
const CPA_WHAT_BAND = line({ needs: [], text: (t) => t("metricHelp.cpaWhatBand") });
const CPA_CALC = line({
  needs: ["spend", "conversions", "cpa"],
  formula: true,
  text: (t, v) => t("metricHelp.cpaCalc", { spend: v.spend, conversions: v.conversions, cpa: v.cpa }),
});
const CPA_DASH = line({ needs: [], text: (t) => t("metricHelp.cpaDash") });
const CPA_ALL = line({ needs: [], text: (t) => t("metricHelp.cpaAll") });
const CPA_NO_COMPARE = line({ needs: [], text: (t) => t("metricHelp.cpaNoCompare") });
const URGENT_WHAT = line({ needs: [], text: (t) => t("metricHelp.urgentWhat") });
const URGENT_CALC = line({
  needs: ["red", "sync", "ads"],
  formula: true,
  text: (t, v) => t("metricHelp.urgentCalc", { red: v.red, sync: v.sync, ads: v.ads }),
});
const URGENT_DASH = line({ needs: [], text: (t) => t("metricHelp.urgentDash") });
const FEED_RED = line({ needs: [], text: (t) => t("metricHelp.feedRed") });
const LEADS_WHAT = line({ needs: [], text: (t) => t("metricHelp.leadsWhat") });
const LEADS_CALC = line({
  needs: ["leads", "byPlatform"],
  formula: true,
  text: (t, v) => t("metricHelp.leadsCalc", { leads: v.leads, byPlatform: v.byPlatform }),
});
const LEADS_EVENTS = line({ needs: [], text: (t) => t("metricHelp.leadsEvents") });
const LEADS_DASH = line({ needs: [], text: (t) => t("metricHelp.leadsDash") });
const CONV_RATE_WHAT = line({ needs: [], text: (t) => t("metricHelp.convRateWhat") });
const CONV_RATE_CALC = line({
  needs: ["conversions", "clicks", "rate"],
  formula: true,
  text: (t, v) => t("metricHelp.convRateCalc", { conversions: v.conversions, clicks: v.clicks, rate: v.rate }),
});
const CONV_RATE_DASH = line({ needs: [], text: (t) => t("metricHelp.convRateDash") });
const CONV_RATE_OVER = line({ needs: [], text: (t) => t("metricHelp.convRateOver") });
const DATA_THROUGH_WHAT = line({ needs: [], text: (t) => t("metricHelp.dataThroughWhat") });
const DATA_THROUGH_RULE = line({ needs: [], text: (t) => t("metricHelp.dataThroughRule") });
const DATA_THROUGH_OTHERS = line({ needs: [], text: (t) => t("metricHelp.dataThroughOthers") });
const DATA_THROUGH_NOTICE = line({ needs: [], text: (t) => t("metricHelp.dataThroughNotice") });
const FUNNEL_WHAT = line({ needs: [], text: (t) => t("metricHelp.funnelWhat") });
const FUNNEL_CTR = line({
  needs: ["clicks", "impressions", "ctr"],
  formula: true,
  text: (t, v) => t("metricHelp.funnelCtr", { clicks: v.clicks, impressions: v.impressions, ctr: v.ctr }),
});
const FUNNEL_STEPS = line({ needs: [], text: (t) => t("metricHelp.funnelSteps") });
const FUNNEL_RANGE = line({
  needs: ["range"],
  text: (t, v) => t("metricHelp.funnelRange", { range: v.range }),
});
const FUNNEL_DELIVERY = line({ needs: [], text: (t) => t("metricHelp.funnelDelivery") });
const TREND_WHAT = line({ needs: [], text: (t) => t("metricHelp.trendWhat") });
const TREND_EXAMPLE = line({
  needs: ["lastDate", "lastSpend", "lastConversions"],
  formula: true,
  text: (t, v) => t("metricHelp.trendExample", { lastDate: v.lastDate, lastSpend: v.lastSpend, lastConversions: v.lastConversions }),
});
const TREND_DAYS = line({
  needs: ["days"],
  text: (t, v) => t("metricHelp.trendDays", { days: v.days }),
});
const TREND_TAIL = line({ needs: [], text: (t) => t("metricHelp.trendTail") });
const BUDGET_WHAT = line({ needs: [], text: (t) => t("metricHelp.budgetWhat") });
const BUDGET_CALC = line({
  needs: ["running", "total", "runningPct", "stopped", "stoppedPct"],
  formula: true,
  text: (t, v) => t("metricHelp.budgetCalc", { running: v.running, total: v.total, runningPct: v.runningPct, stopped: v.stopped, stoppedPct: v.stoppedPct }),
});
const BUDGET_RANGE = line({
  needs: ["range"],
  text: (t, v) => t("metricHelp.budgetRange", { range: v.range }),
});
const BUDGET_STATUS = line({ needs: [], text: (t) => t("metricHelp.budgetStatus") });
const BUDGET_SPENT = line({ needs: [], text: (t) => t("metricHelp.budgetSpent") });
const CONTRACTS_WHAT = line({ needs: [], text: (t) => t("metricHelp.contractsWhat") });
const CONTRACTS_EXAMPLE = line({
  needs: ["exMetric", "exRate", "exAgreed"],
  formula: true,
  text: (t, v) => t("metricHelp.contractsExample", { exMetric: v.exMetric, exRate: v.exRate, exAgreed: v.exAgreed }),
});
const CONTRACTS_EXCLUDED = line({ needs: [], text: (t) => t("metricHelp.contractsExcluded") });
const CONTRACTS_VERDICT = line({ needs: [], text: (t) => t("metricHelp.contractsVerdict") });
const TABLE_WHAT = line({ needs: [], text: (t) => t("metricHelp.tableWhat") });
const TABLE_EXAMPLE = line({
  needs: ["exPlatform", "exSpend", "exConversions", "exCpa"],
  formula: true,
  text: (t, v) => t("metricHelp.tableExample", { exPlatform: v.exPlatform, exSpend: v.exSpend, exConversions: v.exConversions, exCpa: v.exCpa }),
});
const TABLE_RANGE = line({
  needs: ["range"],
  text: (t, v) => t("metricHelp.tableRange", { range: v.range }),
});
const TABLE_DASH = line({ needs: [], text: (t) => t("metricHelp.tableDash") });
const TABLE_COMPARE = line({ needs: [], text: (t) => t("metricHelp.tableCompare") });
const SYSTEM_WHAT = line({ needs: [], text: (t) => t("metricHelp.systemWhat") });
const SYSTEM_DISCONNECTED = line({ needs: [], text: (t) => t("metricHelp.systemDisconnected") });
const SYSTEM_STUCK = line({ needs: [], text: (t) => t("metricHelp.systemStuck") });
const SYSTEM_FAILED = line({ needs: [], text: (t) => t("metricHelp.systemFailed") });
const SYSTEM_WHEN = line({ needs: [], text: (t) => t("metricHelp.systemWhen") });
const SYNC_WHAT = line({ needs: [], text: (t) => t("metricHelp.syncWhat") });
const SYNC_FALLBACK = line({ needs: [], text: (t) => t("metricHelp.syncFallback") });
const SYNC_COLOURS = line({ needs: [], text: (t) => t("metricHelp.syncColours") });
const SYNC_FAILED_LINE = line({ needs: [], text: (t) => t("metricHelp.syncFailedLine") });
const SYNC_NOW_LINE = line({ needs: [], text: (t) => t("metricHelp.syncNowLine") });
const DQ_WHAT = line({ needs: [], text: (t) => t("metricHelp.dqWhat") });
const DQ_BLOCKING = line({ needs: [], text: (t) => t("metricHelp.dqBlocking") });
const DQ_PARTIAL = line({ needs: [], text: (t) => t("metricHelp.dqPartial") });
const DQ_STALE = line({ needs: [], text: (t) => t("metricHelp.dqStale") });

export const METRIC_HELP = {
  systemStatus: entry({
    page: "dashboard",
    anchor: "limits",
    title: (t) => t("health.panelLabel"),
    lines: [SYSTEM_WHAT, SYSTEM_DISCONNECTED, SYSTEM_STUCK, SYSTEM_FAILED, SYSTEM_WHEN],
  }),
  bandSpend: entry({
    page: "dashboard",
    anchor: "platforms",
    title: (t) => t("home.totalSpend"),
    where: WHERE_BAND,
    lines: [SPEND_WHAT, SPEND_CALC, RANGE_SELECTED, COMPARE_PREV, SPEND_NO_COMPARE],
  }),
  bandConversions: entry({
    page: "dashboard",
    anchor: "def-conversions",
    title: (t) => t("home.conversions"),
    where: WHERE_BAND,
    lines: [CONVERSIONS_WHAT, CONVERSIONS_BASKET, RANGE_SELECTED, COMPARE_PREV, CONVERSIONS_NO_COMPARE],
  }),
  bandCpa: entry({
    page: "dashboard",
    anchor: "def-cpa",
    title: (t) => t("home.costPerConversion"),
    where: WHERE_BAND,
    lines: [CPA_WHAT_BAND, CPA_CALC, RANGE_SELECTED, COMPARE_PREV, CPA_NO_COMPARE],
  }),
  bandUrgent: entry({
    page: "dashboard",
    anchor: "limits",
    title: (t) => t("home.bandAttention"),
    lines: [URGENT_WHAT, URGENT_CALC, FEED_RED, RANGE_LAST30, URGENT_DASH],
  }),
  dataThrough: entry({
    page: "dashboard",
    anchor: "coverage",
    title: (t) => t("metricHelp.dataThroughTitle"),
    lines: [DATA_THROUGH_WHAT, DATA_THROUGH_RULE, RANGE_SELECTED, DATA_THROUGH_OTHERS, DATA_THROUGH_NOTICE],
  }),
  syncStatus: entry({
    page: "dashboard",
    anchor: "coverage",
    title: (t) => t("home.syncStatusTitle"),
    lines: [SYNC_WHAT, SYNC_FALLBACK, SYNC_COLOURS, SYNC_FAILED_LINE, SYNC_NOW_LINE],
  }),
  funnel: entry({
    page: "dashboard",
    anchor: "def-ctr",
    title: (t) => t("home.conversionFunnel"),
    lines: [FUNNEL_WHAT, FUNNEL_CTR, FUNNEL_STEPS, FUNNEL_RANGE, FUNNEL_DELIVERY],
  }),
  trend: entry({
    page: "dashboard",
    anchor: "coverage",
    title: (t) => t("home.trendTitle"),
    lines: [TREND_WHAT, TREND_EXAMPLE, RANGE_SELECTED, TREND_DAYS, TREND_TAIL],
  }),
  kpiSpend: entry({
    page: "dashboard",
    anchor: "platforms",
    title: (t) => t("home.totalSpend"),
    where: WHERE_KPI,
    lines: [SPEND_WHAT, SPEND_CALC, RANGE_SELECTED, SPEND_DASH, SPEND_STOPPED],
  }),
  kpiLeads: entry({
    page: "dashboard",
    anchor: "def-leads",
    title: (t) => t("home.realLeads"),
    lines: [LEADS_WHAT, LEADS_CALC, RANGE_SELECTED, LEADS_EVENTS, LEADS_DASH],
  }),
  kpiConversions: entry({
    page: "dashboard",
    anchor: "def-conversions",
    title: (t) => t("home.conversions"),
    where: WHERE_KPI,
    lines: [CONVERSIONS_WHAT, CONVERSIONS_BASKET, RANGE_SELECTED, CONVERSIONS_FRACTIONAL],
  }),
  kpiCpa: entry({
    page: "dashboard",
    anchor: "def-cpa",
    title: (t) => t("home.costPerConversion"),
    where: WHERE_KPI,
    lines: [CPA_WHAT, CPA_CALC, RANGE_SELECTED, CPA_DASH, CPA_ALL],
  }),
  kpiConvRate: entry({
    page: "dashboard",
    anchor: "def-cvr",
    title: (t) => t("home.convRate"),
    lines: [CONV_RATE_WHAT, CONV_RATE_CALC, RANGE_SELECTED, CONV_RATE_DASH, CONV_RATE_OVER],
  }),
  budgetSplit: entry({
    page: "dashboard",
    anchor: "definitions",
    title: (t) => t("budgetInPlay.title"),
    lines: [BUDGET_WHAT, BUDGET_CALC, BUDGET_RANGE, BUDGET_STATUS, BUDGET_SPENT],
  }),
  contracts: entry({
    page: "dashboard",
    anchor: "definitions",
    title: (t) => t("contractSummary.title"),
    lines: [CONTRACTS_WHAT, CONTRACTS_EXAMPLE, RANGE_LAST30, CONTRACTS_EXCLUDED, CONTRACTS_VERDICT],
  }),
  platformTable: entry({
    page: "dashboard",
    anchor: "platforms",
    title: (t) => t("home.platformPerformance"),
    lines: [TABLE_WHAT, TABLE_EXAMPLE, TABLE_RANGE, TABLE_DASH, TABLE_COMPARE],
  }),
  dataQuality: entry({
    page: "dashboard",
    anchor: "limits",
    title: (t) => t("mmm.qualityTitle"),
    lines: [DQ_WHAT, RANGE_ALL_TIME, DQ_BLOCKING, DQ_PARTIAL, DQ_STALE],
  }),
} as const;

export type MetricHelpId = keyof typeof METRIC_HELP;
type NeedsOf<L> = L extends HelpLine<infer P> ? P : never;
export type MetricHelpValues<Id extends MetricHelpId> = Readonly<
  Record<NeedsOf<(typeof METRIC_HELP)[Id]["lines"][number]>, string>
>;
export type MetricHelpProps = {
  [Id in MetricHelpId]: [keyof MetricHelpValues<Id>] extends [never]
    ? { id: Id; values?: undefined }
    : { id: Id; values: MetricHelpValues<Id> };
}[MetricHelpId];

export interface ResolvedLine {
  readonly text: string;
  readonly formula: boolean;
}
export interface ResolvedHelp {
  /** The on-screen label: the panel's visible title and its accessible name. */
  readonly title: string;
  /** The trigger's accessible name, unique on its page. */
  readonly label: string;
  readonly anchor: HelpAnchor;
  readonly lines: readonly ResolvedLine[];
}

const usable = (v: string | undefined): boolean =>
  typeof v === "string" && v.trim() !== "" && v !== NOT_MEASURED;

/** Pure. A line is shown only when every value it needs is known. */
export const resolveMetricHelp = (
  t: HelpT,
  id: MetricHelpId,
  values: Readonly<Record<string, string>> = {},
): ResolvedHelp => {
  const e: MetricHelpEntry = METRIC_HELP[id];
  const title = e.title(t);
  return {
    title,
    label: e.where
      ? t("metricHelp.triggerAt", { metric: title, where: e.where(t) })
      : t("metricHelp.trigger", { metric: title }),
    anchor: e.anchor,
    lines: e.lines
      .filter((l) => l.needs.every((n) => usable(values[n])))
      .map((l) => ({ text: l.text(t, values), formula: l.formula === true })),
  };
};

